// Dashboard Component for Agentcy1
// Post-authentication interface with browser-in-place agentic AI

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot, Loader2, CheckCircle2, AlertCircle, Clock, Zap,
  TrendingUp, Database, Globe, Code, Search, Upload,
  Play, Pause, XCircle, Eye, ThumbsUp, Coins, Crown
} from 'lucide-react';

export function Dashboard({ user, onLogout }) {
  const [query, setQuery] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [estimatedCost, setEstimatedCost] = useState(null);

  // Load user data on mount
  useEffect(() => {
    loadTokenBalance();
    loadTasks();
    loadPendingApprovals();
  }, []);

  // Load token balance
  const loadTokenBalance = async () => {
    try {
      const response = await fetch('https://agentcy-workers.pounds1.workers.dev/api/tokens/balance', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTokenBalance(data.data);
      }
    } catch (error) {
      console.error('Error loading token balance:', error);
    }
  };

  // Load tasks
  const loadTasks = async () => {
    try {
      const response = await fetch('https://agentcy-workers.pounds1.workers.dev/api/v2/tasks?limit=10', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTasks(data.data.tasks);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  // Load pending approvals
  const loadPendingApprovals = async () => {
    try {
      const response = await fetch('https://agentcy-workers.pounds1.workers.dev/api/v2/tasks/approvals/pending', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setPendingApprovals(data.data.approvals);
      }
    } catch (error) {
      console.error('Error loading approvals:', error);
    }
  };

  // Estimate task cost
  const estimateCost = async (taskQuery) => {
    if (!taskQuery.trim()) return;
    
    try {
      const response = await fetch('https://agentcy-workers.pounds1.workers.dev/api/tokens/estimate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: taskQuery })
      });
      const data = await response.json();
      if (data.success) {
        setEstimatedCost(data.data);
      }
    } catch (error) {
      console.error('Error estimating cost:', error);
    }
  };

  // Create new task
  const handleCreateTask = async () => {
    if (!query.trim()) return;

    setProcessing(true);
    
    try {
      const response = await fetch('https://agentcy-workers.pounds1.workers.dev/api/v2/tasks/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query })
      });

      const data = await response.json();
      
      if (data.success) {
        setCurrentTask(data.data);
        setQuery('');
        setEstimatedCost(null);
        await loadTasks();
        await loadTokenBalance();
        
        // Poll for task status
        pollTaskStatus(data.data.taskId);
      } else {
        alert(`Task creation failed: ${data.error}`);
      }
    } catch (error) {
      alert('Error creating task: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Poll task status
  const pollTaskStatus = async (taskId) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`https://agentcy-workers.pounds1.workers.dev/api/v2/tasks/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        
        if (data.success) {
          setCurrentTask(data.data);
          setAgents(data.data.agents || []);
          
          // Stop polling if task is completed or failed
          if (data.data.task.status === 'completed' || data.data.task.status === 'failed') {
            clearInterval(interval);
            await loadTasks();
            await loadPendingApprovals();
          }
        }
      } catch (error) {
        console.error('Error polling task status:', error);
      }
    }, 2000);
  };

  // Approve action
  const handleApproveAction = async (actionId) => {
    try {
      const response = await fetch(`https://agentcy-workers.pounds1.workers.dev/api/v2/tasks/actions/${actionId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        await loadPendingApprovals();
      }
    } catch (error) {
      console.error('Error approving action:', error);
    }
  };

  // Get agent icon
  const getAgentIcon = (type) => {
    const icons = {
      researcher: Search,
      developer: Code,
      deployer: Upload,
      analyzer: TrendingUp
    };
    return icons[type] || Bot;
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-400',
      planning: 'text-blue-400',
      in_progress: 'text-purple-400',
      completed: 'text-green-400',
      failed: 'text-red-400',
      needs_approval: 'text-orange-400',
      active: 'text-cyan-400'
    };
    return colors[status] || 'text-gray-400';
  };

  return (
    <div className="dashboard-container">
      {/* Header */}
      <motion.header 
        className="dashboard-header glass-card"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <div className="dashboard-header-left">
          <h1 className="dashboard-title">
            Agentcy<span className="gradient-text">1</span>
          </h1>
          <div className="user-info">
            <span>{user.email}</span>
            {tokenBalance?.isAdmin && (
              <span className="admin-badge">
                <Crown size={14} />
                God Admin
              </span>
            )}
          </div>
        </div>
        
        <div className="dashboard-header-right">
          {tokenBalance && (
            <div className="token-display">
              <Coins size={20} />
              <span className="token-balance">
                {tokenBalance.unlimited ? '∞' : tokenBalance.balance.toLocaleString()}
              </span>
              <span className="token-label">tokens</span>
            </div>
          )}
          <button onClick={onLogout} className="btn-secondary">
            Logout
          </button>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="dashboard-grid">
        {/* Query Input Section */}
        <motion.section 
          className="query-section glass-card"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="section-title">Deploy AI Agents</h2>
          
          <div className="psychedelic-pill-container">
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                estimateCost(e.target.value);
              }}
              placeholder="Tell me what you need..."
              className="psychedelic-pill"
              disabled={processing}
            />
            <motion.button
              onClick={handleCreateTask}
              disabled={!query.trim() || processing}
              className="btn-primary psychedelic-pill-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {processing ? (
                <><Loader2 className="spin" size={20} /> Processing...</>
              ) : (
                <><Play size={20} /> Deploy Agents</>
              )}
            </motion.button>
          </div>

          {estimatedCost && (
            <motion.div 
              className="cost-estimate"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="cost-info">
                <Zap size={16} />
                <span>Estimated cost: {estimatedCost.estimatedCost} tokens</span>
              </div>
              <div className="agent-preview">
                {estimatedCost.agents.map((agent, i) => (
                  <span key={i} className="agent-chip">
                    {agent.count}x {agent.type}
                  </span>
                ))}
              </div>
            </motion.div>
          )}
        </motion.section>

        {/* Active Task */}
        {currentTask && (
          <motion.section 
            className="active-task glass-card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="section-title">Active Task</h2>
            
            <div className="task-info">
              <div className="task-header">
                <span className={`task-status ${getStatusColor(currentTask.task.status)}`}>
                  {currentTask.task.status}
                </span>
                <span className="task-type">{currentTask.task.task_type}</span>
              </div>
              <p className="task-query">{currentTask.task.query}</p>
            </div>

            {/* Agents */}
            <div className="agents-grid">
              {agents.map((agent) => {
                const Icon = getAgentIcon(agent.agent_type);
                return (
                  <motion.div 
                    key={agent.id}
                    className="agent-card"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    <div className="agent-icon">
                      <Icon size={24} />
                    </div>
                    <div className="agent-info">
                      <div className="agent-type">{agent.agent_type}</div>
                      <div className={`agent-status ${getStatusColor(agent.status)}`}>
                        {agent.status}
                      </div>
                      {agent.current_action && (
                        <div className="agent-action">{agent.current_action}</div>
                      )}
                    </div>
                    <div className="agent-progress">
                      <div className="progress-bar-bg">
                        <motion.div 
                          className="progress-bar-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${agent.progress}%` }}
                        />
                      </div>
                      <span className="progress-text">{agent.progress}%</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.section>
        )}

        {/* Pending Approvals */}
        {pendingApprovals.length > 0 && (
          <motion.section 
            className="approvals-section glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="section-title">
              <AlertCircle size={20} />
              Pending Approvals
            </h2>
            
            <div className="approvals-list">
              {pendingApprovals.map((approval) => (
                <div key={approval.id} className="approval-card">
                  <div className="approval-header">
                    <span className="approval-type">{approval.action_type}</span>
                    <span className="approval-agent">{approval.agent_type}</span>
                  </div>
                  <div className="approval-details">
                    {approval.action_data.description || JSON.stringify(approval.action_data)}
                  </div>
                  <button 
                    onClick={() => handleApproveAction(approval.id)}
                    className="btn-approve"
                  >
                    <ThumbsUp size={16} />
                    Approve
                  </button>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Recent Tasks */}
        <motion.section 
          className="tasks-section glass-card"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="section-title">Recent Tasks</h2>
          
          <div className="tasks-list">
            {tasks.map((task) => (
              <motion.div 
                key={task.id}
                className="task-item"
                whileHover={{ scale: 1.02 }}
              >
                <div className="task-item-header">
                  <span className={`task-status ${getStatusColor(task.status)}`}>
                    {task.status}
                  </span>
                  <span className="task-date">
                    {new Date(task.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="task-item-query">{task.query}</div>
                <div className="task-item-footer">
                  <span className="task-agents">
                    <Bot size={14} />
                    {task.agents_deployed} agents
                  </span>
                  <span className="task-tokens">
                    <Coins size={14} />
                    {task.total_tokens_cost} tokens
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
}
